import applicationService from '@/shared/services/applicationService';

// The server refused the submit without saying why, after the answers were
// saved
export const SUBMIT_FAILED_SAVED_MESSAGE =
    'Your request was not submitted. Your answers are saved, so you can try again now or come back to it later from Support requests.';

// No answer from the submit, and the read-back didn't show it submitted
// (it may still be landing, or the read-back failed too)
export const SUBMIT_UNCONFIRMED_MESSAGE =
    "We couldn't confirm whether your request was submitted. Your answers are saved. Check Support requests before trying again.";

// The save before submit was refused because it's already submitted (by an
// earlier attempt whose answer was lost, or another tab), so what's on this
// page may not be what was sent
export const ALREADY_SUBMITTED_MESSAGE =
    "This application has already been submitted, so it can't be changed here. Open it from Applications to see what was submitted.";

// The save line once it's already submitted, in place of the refused save's
// raw error, so the save buttons still say why nothing was saved
export const ALREADY_SUBMITTED_SAVE_NOTE =
    "Changes can't be saved here because this application has already been submitted.";

// Where Save and finish later lands once it's already submitted, with
// nothing left to save
export const ALREADY_SUBMITTED_LEFT_MESSAGE =
    'This application had already been submitted, so nothing more was saved. Open it to see what was submitted.';

const SUBMIT_FAILED_MESSAGE = 'Failed to submit the request. Please try again.';

// Statuses an application is still in until a submit goes through
const NOT_SUBMITTED = ['draft', 'revision_requested', 'withdrawn'];

// 'submitted', 'not_submitted', or 'unknown' when it can't be read back
async function readBack(applicationId) {
    if (!applicationId) return 'not_submitted';
    try {
        const app = await applicationService.getApplicationById(applicationId);
        return app?.status && !NOT_SUBMITTED.includes(app.status)
            ? 'submitted'
            : 'not_submitted';
    } catch {
        return 'unknown';
    }
}

/**
 * What to tell someone after their submit threw (#394).
 *
 * `saved` is whether the save just before the submit went through.
 *
 * A 4xx after a good save is a refusal, so its message stands. A 500 comes
 * back once the server has finished, but it can come after the submit was
 * saved (a failed notice), so it's read back and a draft there means it
 * failed. With no answer or a 502-504 from the proxy the submit may still
 * be landing, so a read-back without it isn't proof it failed.
 *
 * Returns { submitted: true } or { message }, with alreadySubmitted set
 * when nothing on the page can be saved any more.
 */
export async function submitFailureOutcome(err, { saved, applicationId }) {
    const serverMessage = err?.response?.data?.message;
    const status = err?.response?.status;
    if (saved && err?.response && !(status >= 500)) {
        return { message: serverMessage || SUBMIT_FAILED_SAVED_MESSAGE };
    }
    const outcome = await readBack(applicationId);
    if (outcome === 'submitted') {
        return saved
            ? { submitted: true }
            : { message: ALREADY_SUBMITTED_MESSAGE, alreadySubmitted: true };
    }
    if (!saved) return { message: serverMessage || SUBMIT_FAILED_MESSAGE };
    if (status === 500 && outcome === 'not_submitted') {
        return { message: SUBMIT_FAILED_SAVED_MESSAGE };
    }
    return { message: SUBMIT_UNCONFIRMED_MESSAGE };
}

/**
 * Saves the answers, then submits them, and works out what happened when
 * either step throws. Shared by the create wizard and Edit and resubmit.
 *
 * `save(payload)` saves through the page's save queue, and `getId()` reads
 * the application id after the save (the first save creates it).
 *
 * Returns { submitted: true } or { message, alreadySubmitted?, error }.
 */
export async function saveAndSubmit({ save, payload, getId }) {
    let saved = false;
    try {
        await save(payload);
        saved = true;
        await applicationService.submitApplication(getId());
        return { submitted: true };
    } catch (error) {
        const outcome = await submitFailureOutcome(error, {
            saved,
            applicationId: getId(),
        });
        return outcome.submitted ? outcome : { ...outcome, error };
    }
}
